/**
 * zod schemas for the primitive formats every row and patch is built from.
 *
 * Each delegates to the one validator that defines the format (`isValidHlc`,
 * `isValidFieldPath`, …), so there is exactly one definition of each, and
 * `@acp/api-contract` builds its wire schemas from these rather than
 * re-declaring them.
 */
import { z } from 'zod';
import { isValidBase64 } from './base64.js';
import { isValidHlc } from './hlc.js';
import { isValidFieldPath } from './lww.js';
import { isValidOrd } from './ordering.js';
import { isValidUlid } from './ulid.js';

/** A JSON value: what a row field holds. */
export const jsonValueSchema = z.json();
export type JsonValue = z.infer<typeof jsonValueSchema>;

/** An HLC string, `<13-digit ms>-<5-digit counter>-<nodeId>`. */
export const hlcSchema = z.string().refine(isValidHlc, { message: 'not an HLC "<ms>-<counter>-<nodeId>"' });

/** A dotted field path: `title`, `params.vs.clip`. */
export const fieldPathSchema = z.string().refine(isValidFieldPath, { message: 'not a dotted field path' });

/** A canonical ULID: row ids, rundown ids, the `changedAt` cursor. */
export const ulidSchema = z.string().refine(isValidUlid, { message: 'not a ULID' });

/** A sibling-order key (`ordering.ts`). */
export const ordSchema = z.string().refine(isValidOrd, { message: 'not a fractional-index order key' });

/** Padded base64: an embedded Yjs state or update. */
export const base64Schema = z.string().refine(isValidBase64, { message: 'not padded base64' });

/**
 * An opaque writer / operation id (`origin`, `clientOpId`): bounded, and no
 * whitespace, because both are logged and used as keys.
 */
export const opaqueIdSchema = z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/, { message: 'not an id [A-Za-z0-9._:-]{1,128}' });

/**
 * A duration in milliseconds. Capped at 24 h: no part, piece or transition in a
 * live production is longer, and a cap keeps a typo (a value in microseconds)
 * from being accepted as a 30-year duration.
 */
export const MAX_DURATION_MS = 24 * 60 * 60 * 1000;
export const durationMsSchema = z.number().int().nonnegative().max(MAX_DURATION_MS);

/** Milliseconds since the Unix epoch. */
export const epochMsSchema = z.number().int().nonnegative();
